import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import './MyMeetings.css';

const GuestMeetings = ({ onBack, token }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('2weeks');
  const [searchTerm, setSearchTerm] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Fetch meetings from API
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        setLoading(true);
        setError('');

        const apiUrl = `${process.env.REACT_APP_API_URL}/api/guests/get/meeting`;
        console.log('Fetching guest meetings from:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });

        console.log('API Response status:', response.status);

        const data = await response.json();
        console.log('API Response:', data);

        if (response.ok && data.data && Array.isArray(data.data)) {
          // Transform API data to match our meeting structure
          const transformedMeetings = data.data.map(apiMeeting => {
            const meetingDate = new Date(apiMeeting.datetime);
            const dateStr = `${meetingDate.getFullYear()}-${String(meetingDate.getMonth() + 1).padStart(2, '0')}-${String(meetingDate.getDate()).padStart(2, '0')}`;
            const timeStr = `${String(meetingDate.getHours()).padStart(2, '0')}:${String(meetingDate.getMinutes()).padStart(2, '0')}`;

            return {
              id: apiMeeting.id,
              date: dateStr,
              time: timeStr,
              datetime: apiMeeting.datetime,
              title: apiMeeting.meeting_agenda,
              attendeeName: apiMeeting.attendee_name,
              attendeePhone: apiMeeting.attendee_phone,
              location: apiMeeting.location,
              createdAt: apiMeeting.created_at,
            };
          });

          setMeetings(transformedMeetings);
          console.log('Transformed meetings:', transformedMeetings);
        } else {
          setError(data.message || 'Failed to fetch meetings');
        }
      } catch (err) {
        console.error('Error fetching meetings:', err);
        setError(err.message || 'Failed to fetch meetings');
      } finally {
        setLoading(false);
      }
    };

    fetchMeetings();
  }, [token]);

  // Get the number of days in a month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get the first day of the month
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Get all meetings for a specific date
  const getMeetingsForDate = useCallback((date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return meetings.filter(m => m.date === dateStr);
  }, [meetings]);

  // Get meetings count for each date
  const getMeetingsCountForDate = (date) => {
    return getMeetingsForDate(date).length;
  };

  // Handle previous month
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  // Handle next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Handle date click
  const handleDateClick = (day) => {
    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  // Handle meeting deletion
  const handleDeleteMeeting = async (meetingId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this meeting?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setDeleting(meetingId);
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/guests/delete/meeting/${meetingId}`;
      console.log('Deleting guest meeting from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      console.log('Delete response status:', response.status);

      const data = await response.json();
      console.log('Delete response:', data);

      if (response.ok) {
        // Remove the deleted meeting from the list
        setMeetings(meetings.filter(m => m.id !== meetingId));
        console.log('Meeting deleted successfully');
        Swal.fire({
          title: 'Deleted!',
          text: 'Meeting has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to delete meeting',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error deleting meeting:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to delete meeting',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setDeleting(null);
    }
  };

  // Handle edit meeting
  const handleEditMeeting = (meeting) => {
    setEditingMeeting({
      id: meeting.id,
      meeting_agenda: meeting.title,
      attendee_name: meeting.attendeeName,
      attendee_phone: meeting.attendeePhone,
      location: meeting.location,
      datetime: meeting.datetime
    });
    setShowEditModal(true);
  };

  // Handle update meeting
  const handleUpdateMeeting = async () => {
    if (!editingMeeting.meeting_agenda || !editingMeeting.attendee_name || !editingMeeting.datetime) {
      Swal.fire({
        title: 'Warning',
        text: 'Please fill in all required fields',
        icon: 'warning',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setUpdating(true);
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/guests/update/meetings/${editingMeeting.id}`;
      console.log('Updating guest meeting:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          meeting_agenda: editingMeeting.meeting_agenda,
          attendee_name: editingMeeting.attendee_name,
          attendee_phone: editingMeeting.attendee_phone,
          location: editingMeeting.location,
          datetime: editingMeeting.datetime
        }),
      });

      const data = await response.json();
      console.log('Update response:', data);

      if (response.ok) {
        // Update the meeting in the list
        const updatedMeetings = meetings.map(m => {
          if (m.id === editingMeeting.id) {
            const meetingDate = new Date(editingMeeting.datetime);
            const dateStr = `${meetingDate.getFullYear()}-${String(meetingDate.getMonth() + 1).padStart(2, '0')}-${String(meetingDate.getDate()).padStart(2, '0')}`;
            const timeStr = `${String(meetingDate.getHours()).padStart(2, '0')}:${String(meetingDate.getMinutes()).padStart(2, '0')}`;
            
            return {
              ...m,
              title: editingMeeting.meeting_agenda,
              attendeeName: editingMeeting.attendee_name,
              attendeePhone: editingMeeting.attendee_phone,
              location: editingMeeting.location,
              datetime: editingMeeting.datetime,
              date: dateStr,
              time: timeStr
            };
          }
          return m;
        });
        
        setMeetings(updatedMeetings);
        setShowEditModal(false);
        setEditingMeeting(null);
        
        Swal.fire({
          title: 'Success!',
          text: 'Meeting updated successfully!',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to update meeting',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error updating meeting:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to update meeting',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Get selected date's meetings
  const selectedMeetings = React.useMemo(() => {
    let filtered = getMeetingsForDate(selectedDate);
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(m =>
        // Search by Meeting Agenda
        (m.title && m.title.toLowerCase().includes(search)) ||
        // Search by Name
        (m.attendeeName && m.attendeeName.toLowerCase().includes(search)) ||
        // Search by Phone Number
        (m.attendeePhone && m.attendeePhone.toLowerCase().includes(search))
      );
    }
    return filtered;
  }, [selectedDate, searchTerm, getMeetingsForDate]);

  // Render calendar grid
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="calendar-day empty"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const meetingsCount = getMeetingsCountForDate(date);
      const isSelected =
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentDate.getMonth() &&
        selectedDate.getFullYear() === currentDate.getFullYear();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateClick(day)}
        >
          <span className="day-number">{day}</span>
          {meetingsCount > 0 && (
            <span className="meetings-badge">{meetingsCount}</span>
          )}
        </div>
      );
    }

    return days;
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="my-meetings-container">
      {/* Header */}
      <div className="meetings-header">
        <button className="back-button" onClick={onBack}>
          ← 
        </button>
        <h2>📅 My Meetings</h2>
        <div style={{ width: '80px' }}></div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search by agenda, name, or phone number..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="meetings-content">
        {/* Calendar Section */}
        <div className="calendar-section">
          {/* Month Navigation */}
          <div className="month-navigation">
            <button className="nav-button" onClick={handlePrevMonth}>
              ←
            </button>
            <span className="month-label">{monthName}</span>
            <button className="nav-button" onClick={handleNextMonth}>
              →
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="view-mode-toggle">
            <button
              className={`mode-button ${viewMode === '2weeks' ? 'active' : ''}`}
              onClick={() => setViewMode('2weeks')}
            >
              2 weeks
            </button>
            <button
              className={`mode-button ${viewMode === '1week' ? 'active' : ''}`}
              onClick={() => setViewMode('1week')}
            >
              1 week
            </button>
            <button
              className={`mode-button ${viewMode === '1month' ? 'active' : ''}`}
              onClick={() => setViewMode('1month')}
            >
              1 month
            </button>
          </div>

          {/* Day Headers */}
          <div className="calendar-header">
            <div className="day-header">Sun</div>
            <div className="day-header">Mon</div>
            <div className="day-header">Tue</div>
            <div className="day-header">Wed</div>
            <div className="day-header">Thu</div>
            <div className="day-header">Fri</div>
            <div className="day-header">Sat</div>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {renderCalendar()}
          </div>
        </div>

        {/* Meetings List Section */}
        <div className="meetings-list-section">
          <div className="selected-date-info">
            <h3>
              {selectedDate.toLocaleDateString('default', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </h3>
            <span className="meeting-count">
              {selectedMeetings.length} {selectedMeetings.length === 1 ? 'meeting' : 'meetings'}
            </span>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>Loading your meetings...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>⚠️ {error}</p>
            </div>
          ) : selectedMeetings.length > 0 ? (
            <div className="meetings-list">
              {selectedMeetings.map(meeting => (
                <div key={meeting.id} className="meeting-item">
                  <div className="meeting-header">
                    <div className="meeting-title-section">
                      <span className="meeting-title">{meeting.title}</span>
                      <span className="meeting-time">
                        <span className="time-icon">🕐</span>
                        {meeting.time}
                      </span>
                    </div>
                    <div className="meeting-actions">
                      <button
                        className="edit-button"
                        onClick={() => handleEditMeeting(meeting)}
                        title="Edit meeting"
                      >
                        ✏️
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteMeeting(meeting.id)}
                        disabled={deleting === meeting.id}
                        title="Delete meeting"
                      >
                        {deleting === meeting.id ? '⏳' : '🗑️'}
                      </button>
                    </div>
                  </div>

                  <div className="meeting-details">
                    <div className="detail-row">
                      <span className="detail-icon">📅</span>
                      <span className="detail-label">Date:</span>
                      <span className="detail-value">
                        {new Date(meeting.datetime).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-icon">⏰</span>
                      <span className="detail-label">Time:</span>
                      <span className="detail-value">{meeting.time}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-icon">📝</span>
                      <span className="detail-label">Agenda:</span>
                      <span className="detail-value">{meeting.title}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-icon">👤</span>
                      <span className="detail-label">Attendee:</span>
                      <span className="detail-value">{meeting.attendeeName}</span>
                    </div>
                    {meeting.attendeePhone && (
                      <div className="detail-row">
                        <span className="detail-icon">📱</span>
                        <span className="detail-label">Phone:</span>
                        <span className="detail-value">{meeting.attendeePhone}</span>
                      </div>
                    )}
                    {meeting.location && (
                      <div className="detail-row">
                        <span className="detail-icon">📍</span>
                        <span className="detail-label">Location:</span>
                        <span className="detail-value">{meeting.location}</span>
                      </div>
                    )}
                   
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-meetings">
              <p>No meetings scheduled for this date</p>
              {searchTerm && (
                <button
                  className="clear-search-button"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Meeting Modal */}
      {showEditModal && editingMeeting && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-meeting-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Meeting</h3>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Meeting Agenda *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingMeeting.meeting_agenda}
                  onChange={(e) => setEditingMeeting({
                    ...editingMeeting,
                    meeting_agenda: e.target.value
                  })}
                  placeholder="Enter meeting agenda"
                />
              </div>

              <div className="form-group">
                <label>Attendee Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingMeeting.attendee_name}
                  onChange={(e) => setEditingMeeting({
                    ...editingMeeting,
                    attendee_name: e.target.value
                  })}
                  placeholder="Enter attendee name"
                />
              </div>

              <div className="form-group">
                <label>Attendee Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={editingMeeting.attendee_phone || ''}
                  onChange={(e) => setEditingMeeting({
                    ...editingMeeting,
                    attendee_phone: e.target.value
                  })}
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingMeeting.location || ''}
                  onChange={(e) => setEditingMeeting({
                    ...editingMeeting,
                    location: e.target.value
                  })}
                  placeholder="Enter location"
                />
              </div>

              <div className="form-group">
                <label>Date & Time *</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={editingMeeting.datetime ? editingMeeting.datetime.slice(0, 16) : ''}
                  onChange={(e) => setEditingMeeting({
                    ...editingMeeting,
                    datetime: e.target.value
                  })}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingMeeting(null);
                }}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleUpdateMeeting}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Update Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestMeetings;
